import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { renderToStaticMarkup } from 'react-dom/server';
import { nanoid } from 'nanoid';

import { useGetImageFullPath } from 'helpers';

import { MetaTagsAll, ImageMeta, VideoMeta, FAQMeta } from '../../interfaces';
import { PreloadEssentials } from './components/PreloadEssentials';

type Props = {
  relatedImagePath?: string;
  metaTags: MetaTagsAll;
  images?: ImageMeta[];
  videos?: VideoMeta[];
  faq?: FAQMeta[];
  isArticle?: boolean;
};

export const SEO = ({
  metaTags,
  relatedImagePath = '',
  images = [],
  videos = [],
  faq = [],
  isArticle = false,
}: Props) => {
  const data = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          title
          description
          siteUrl
        }
      }
    }
  `);

  const talenteerByItransitionLogo = useGetImageFullPath('talenteer.png');

  const defaults = data.site.siteMetadata;

  const { siteUrl } = defaults;
  const meta = [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, maximum-scale=1, shrink-to-fit=no',
    },
    {
      name: 'description',
      content: metaTags.description,
    },
    {
      name: 'robots',
      content: metaTags?.robots,
    },
    {
      property: 'og:title',
      content: metaTags.title,
    },
    {
      property: 'og:description',
      content: metaTags.description || defaults.description,
    },
    {
      property: 'og:image',
      content: siteUrl + (relatedImagePath || ''),
    },
    {
      property: 'og:url',
      content: metaTags.canonicalUrl,
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: metaTags?.ogTitle || metaTags?.title || defaults.title,
    },
    {
      name: 'twitter:image',
      content: siteUrl + (relatedImagePath || ''),
    },
  ];

  const localBusinessJson = JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Itransition',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '3900 S. Wadsworth Blvd., Suite 485',
        addressLocality: 'Denver',
        addressRegion: 'CO',
        postalCode: '80235',
      },
      pricerange: '$$$',
      email: 'info@itransition.com',
      telePhone: '+1 720 207 2820',
      openingHours: 'Mo,Tu,We,Th,Fr, 09:00-18:00',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
      image: 'https://www.itransition.com/media/s3fs-public/images/Itransition.png',
    },
    null,
    '\t',
  );

  const productJson = JSON.stringify(
    {
      '@context': 'http://schema.org',
      '@type': 'product',
      name: 'Talenteer',
      image: talenteerByItransitionLogo,
      description: metaTags.description,
      brand: 'Itransition',
      mpn: 'Itransition',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '39',
      },
      review: {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '4.9',
          bestRating: '5',
          worstRating: '4',
        },
        author: {
          '@type': 'Person',
          name: 'Itransition',
        },
      },
      url: `${process.env.SITE_URL}`,
    },
    null,
    '\t',
  );

  const logoJson = JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      url: `${process.env.SITE_URL}/`,
      logo: talenteerByItransitionLogo,
      name: 'Talenteer',
    },
    null,
    '\t',
  );

  const imageObjectJson = images.map((image) =>
    JSON.stringify(
      {
        '@context': 'https://schema.org/',
        '@type': 'ImageObject',
        contentUrl: image.imageUrl,
        description: image.imageDescription,
        name: image.imageName,
        creator: {
          '@type': 'Organization',
          name: 'Itransition',
        },
      },
      null,
      '\t',
    ),
  );

  const videoObjectJson = videos.map((video) =>
    JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.videoName,
      description: video.videoDescription,
      uploadDate: '2023-04-12T08:00:00+08:00',
      duration: 'PT1M58S',
      contentUrl: video.videoUrl,
      thumbnailUrl: video.videoPreview,
    }),
  );

  const ArticleJson = JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: metaTags.title,
      image: siteUrl + relatedImagePath,
      datePublished: metaTags.createdDate && new Date(metaTags.createdDate),
      dateModified: metaTags.changedDate
        ? new Date(metaTags.changedDate)
        : metaTags.createdDate && new Date(metaTags.createdDate),
      author: metaTags.author
        ? [
            {
              '@type': 'Person',
              name: metaTags.author!.name,
              jobTitle: metaTags.author!.position,
              ...(metaTags.author.link && { url: metaTags.author.link }),
            },
          ]
        : [
            {
              '@type': 'Organization',
              name: 'Itransition',
              url: 'https://www.itransition.com',
            },
          ],
    },
    null,
    '\t',
  );

  const FAQJson = faq.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: renderToStaticMarkup(
        <div className="accordion-content">
          {item.answer && <p>{item.answer}</p>}
          <div dangerouslySetInnerHTML={{ __html: item.content || '' }} />
        </div>,
      )
        .replace(/(<([^>]+)>)/gi, ' ')
        .replace(/  +/g, ' ')
        .trim(),
    },
  }));

  const FAQMarkup = JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [FAQJson],
    },
    null,
    '\t',
  );

  const schemaMarkupScripts = [
    {
      type: 'application/ld+json',
      innerHTML: `${localBusinessJson}`,
    },
    {
      type: 'application/ld+json',
      innerHTML: `${productJson}`,
    },
    {
      type: 'application/ld+json',
      innerHTML: `${logoJson}`,
    },
    {
      type: 'application/ld+json',
      innerHTML: `${imageObjectJson}`,
    },
    {
      type: 'application/ld+json',
      innerHTML: `${videoObjectJson}`,
    },
  ];

  return (
    <>
      <title>{metaTags.title}</title>
      {meta.map((metaData) => (
        <meta content={metaData.content} name={metaData.name} property={metaData.property} key={nanoid()} />
      ))}
      <link rel="canonical" href={metaTags.canonicalUrl} />
      {relatedImagePath && <link rel="preload" type="image/webp" as="image" href={siteUrl + relatedImagePath} />}
      <PreloadEssentials />
      {schemaMarkupScripts.map((script) => (
        <script type={script.type} key={nanoid()}>
          {script.innerHTML}
        </script>
      ))}
      {faq.length > 0 && <script type="application/ld+json">{FAQMarkup}</script>}
      {isArticle && (
        <script type="application/ld+json" key={nanoid()}>
          {`${ArticleJson}`}
        </script>
      )}
    </>
  );
};
